import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface PasscodeStatus {
  hasPasscode: boolean;
  failedAttempts?: number;
  lockedUntil?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

async function handleStaleSession() {
  try {
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    // ignore: session already invalid
  }
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
    window.location.replace("/login");
  }
}

async function callPasscodeFunction(action: string, data: Record<string, unknown> = {}) {
  // Guard: if there is no active session, don't even call. Sign out and bounce.
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session) {
    await handleStaleSession();
    // Return a neutral status response so callers don't crash the UI.
    if (action === "status") return { hasPasscode: false };
    throw new Error("Not signed in");
  }

  const { data: result, error } = await supabase.functions.invoke("manage-passcode", {
    body: { action, ...data },
  });

  // FunctionsHttpError surfaces here when the edge function returns non-2xx.
  if (error) {
    const status = (error as any)?.context?.status;
    if (status === 401) {
      await handleStaleSession();
      if (action === "status") return { hasPasscode: false };
    }
    throw error;
  }

  if (result?.error === "Unauthorised") {
    await handleStaleSession();
    if (action === "status") return { hasPasscode: false };
    throw new Error("Unauthorised");
  }

  if (result?.error) {
    const err = new Error(result.error);
    (err as any).status = result.status;
    (err as any).details = result;
    throw err;
  }
  return result;
}

export function usePasscodeStatus() {
  return useQuery<PasscodeStatus>({
    queryKey: ["passcode_status"],
    queryFn: () => callPasscodeFunction("status"),
    staleTime: 30_000,
  });
}

export function useSetPasscode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (passcode: string) => callPasscodeFunction("set", { passcode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["passcode_status"] }),
  });
}

export function useVerifyPasscode() {
  return useMutation({
    mutationFn: ({ passcode, actionAttempted }: { passcode: string; actionAttempted?: string }) =>
      callPasscodeFunction("verify", { passcode, actionAttempted }),
  });
}

export function useChangePasscode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ currentPasscode, newPasscode }: { currentPasscode: string; newPasscode: string }) =>
      callPasscodeFunction("change", { currentPasscode, newPasscode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["passcode_status"] }),
  });
}

export function useRemovePasscode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (currentPasscode: string) =>
      callPasscodeFunction("remove", { currentPasscode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["passcode_status"] }),
  });
}
